import Stepper from "@/components/common/Stepper";
import { BackButton } from "@/features/i18n/formComponents";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { organisationsQueryOptions } from "@/features/auth/api/useOrganisationsQuery";
import { getSpecialtyLabel } from "@/features/i18n/clinicalTermLabels";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { studyByIdQueryOptions } from "@/features/studies/api/selectStudies";
import { StudyInviteQrMark, StudyInviteQrPanel } from "@/features/studies/components/StudyInviteQrPanel";
import { studyInviteSearchSchema } from "@/features/studies/utils/studyInviteSearch";
import { getStudyInviteSteps } from "@/features/studies/utils/studyInviteSteps";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { useStudiesCopy } from "@/features/i18n/studiesCopy";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@patient-watch/ui/card";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";

function StudyInviteQrPage() {
    const { studyId } = Route.useParams();
    const navigate = useNavigate({ from: Route.fullPath });
    const inviteCopy = useInviteCopy();
    const studiesCopy = useStudiesCopy();
    const actionCopy = useActionCopy();
    const { locale } = useAppLocale();
    const qrWrapRef = useRef<HTMLDivElement>(null);
    const [inviteUrl, setInviteUrl] = useState<string | null>(null);

    const { data: study, isLoading } = useQuery(studyByIdQueryOptions(studyId));
    const { data: organisations = [] } = useQuery(organisationsQueryOptions());

    const organisationId = study?.organisation_id?.trim() || undefined;
    const specialty = study?.specialty?.trim() || undefined;
    const organisationName = useMemo(() => {
        if (!organisationId) return undefined;
        return organisations.find((org) => org.organisation_id === organisationId)?.organisation_name;
    }, [organisationId, organisations]);

    const inviteDefaultsSummary = useMemo(() => {
        const parts: string[] = [];
        if (organisationName) parts.push(organisationName);
        if (specialty) parts.push(getSpecialtyLabel(specialty, locale));
        return parts.join(" · ");
    }, [locale, organisationName, specialty]);

    useNav([
        { route: { to: "/studies" }, label: studiesCopy.studies },
        {
            route: { to: "/studies/$studyId", params: { studyId } },
            label: study?.study_name ?? studiesCopy.studies,
        },
        {
            route: { to: "/studies/$studyId/invite", params: { studyId } },
            label: studiesCopy.studyInvite,
        },
        {
            route: { to: "/studies/$studyId/invite/qr", params: { studyId } },
            label: inviteCopy.shareQrCode,
        },
    ]);

    if (isLoading) {
        return <p className="text-muted-foreground text-sm">{studiesCopy.loadingStudy}</p>;
    }

    if (!study) {
        return (
            <section className="space-y-4">
                <ui.Button type="button" variant="outline" onClick={() => void navigate({ to: "/studies" })}>
                    {studiesCopy.backToStudies}
                </ui.Button>
            </section>
        );
    }

    return (
        <section className="space-y-6 py-4 md:py-6 w-full min-w-0">
            <div className="space-y-4">
                <Stepper steps={getStudyInviteSteps(studyId, "qr", studiesCopy)} />
            </div>

            <Card className="bg-transparent dark:bg-transparent shadow-none border-0 overflow-hidden">
                <CardHeader className="space-y-0 px-0 pb-4">
                    <div className="flex sm:flex-row flex-col sm:justify-between items-start sm:items-start gap-6 sm:gap-8">
                        <div className="flex-1 space-y-6 sm:pr-6 md:pr-8 w-full min-w-0 text-left">
                            <div className="space-y-3">
                                <p className="font-semibold text-indigo-600 dark:text-indigo-400 text-xs uppercase tracking-widest">
                                    {studiesCopy.studyInvite}
                                </p>
                                <CardTitle className="font-bold text-slate-900 dark:text-slate-50 text-2xl md:text-3xl normal-case tracking-tight">
                                    {inviteCopy.diaryQrCode}
                                </CardTitle>
                                <CardDescription className="max-w-md text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                                    {studiesCopy.studyInviteQrDescription(study.study_name)}
                                </CardDescription>
                                {inviteDefaultsSummary ? (
                                    <p
                                        className="text-slate-600 dark:text-slate-400 text-sm"
                                        data-testid="study-invite-defaults-summary"
                                    >
                                        {studiesCopy.inviteDefaultsSummary(inviteDefaultsSummary)}
                                    </p>
                                ) : (
                                    <p
                                        className="text-slate-500 dark:text-slate-400 text-sm"
                                        data-testid="study-invite-defaults-empty"
                                    >
                                        {studiesCopy.inviteDefaultsEmpty}
                                    </p>
                                )}
                            </div>
                        </div>

                        {inviteUrl ? (
                            <div className="flex justify-start sm:justify-end sm:pt-0.5 w-full sm:w-auto shrink-0">
                                <StudyInviteQrMark
                                    inviteUrl={inviteUrl}
                                    title={studiesCopy.joinStudyTitle(study.study_name)}
                                    containerRef={qrWrapRef}
                                />
                            </div>
                        ) : null}
                    </div>
                </CardHeader>

                <CardContent className="space-y-6 px-0 pt-2">
                    <StudyInviteQrPanel
                        study={study}
                        organisationId={organisationId}
                        organisationName={organisationName}
                        specialty={specialty}
                        onInviteUrlChange={setInviteUrl}
                    />
                </CardContent>
            </Card>

            <div className="flex flex-row-reverse items-center gap-x-4 mb-8 pt-2 w-full">
                <div className="w-full">
                    <BackButton
                        className="w-full"
                        label={actionCopy.back}
                        onClick={() =>
                            void navigate({
                                to: "/studies/$studyId/invite",
                                params: { studyId },
                            })
                        }
                    />
                </div>
            </div>
        </section>
    );
}

export const Route = createFileRoute("/_app/studies/$studyId/invite/qr")({
    component: StudyInviteQrPage,
    validateSearch: (search) => studyInviteSearchSchema.parse(search),
    loader: async ({ context, params }) => {
        await context.queryClient.ensureQueryData(studyByIdQueryOptions(params.studyId));
    },
});
